import { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Stack,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  X,
  Camera,
  Upload,
  Check,
  Download,
  RefreshCw,
  Cloud,
  Folder,
  Zap,
  Shield,
  ArrowLeft,
} from "lucide-react";
import { getUserProfile } from "../utils/auth";
import { VIRTUAL_TRY_ON_URL } from "../config/apiConfig";
import {
  notifyVirtualTryOnImageLoaded,
  notifyVirtualTryOnFailed,
  notifyVirtualTryOnClosed,
} from "../utils/virtualTryOnAvatarHold";
import { trackVtoChanged } from "../services/uiAgentSync";

const VirtualTryOn = ({ open, onClose, productImage, article_id }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Processing, 3: Result
  const [personImage, setPersonImage] = useState(null);
  const [personImagePreview, setPersonImagePreview] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [showUploadArea, setShowUploadArea] = useState(false);
  const fileInputRef = useRef(null);

  // UI → Agent sync: report the try-on overlay opening/closing (single
  // instrumentation point — covers manual PDP buttons and agent-driven opens).
  useEffect(() => {
    if (!open) return undefined;
    trackVtoChanged(true, article_id);
    return () => trackVtoChanged(false, article_id);
  }, [open, article_id]);

  /** Use local-part of email for custom_model (e.g. emily.parker@gmail.com → emily.parker). */
  const formatCustomModelName = (value) => {
    if (value == null) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    const at = raw.indexOf("@");
    const base = at > 0 ? raw.slice(0, at) : raw;
    const cleaned = base.trim();
    return cleaned || null;
  };

  // Utility to clean image URLs (fix Google Cloud Storage URLs)
  const cleanImageUrl = (url) => {
    if (!url || typeof url !== "string") return url;
    return url
      .trim()
      .replace(/storage\.cloud\.google\.com/g, "storage.googleapis.com");
  };

  // Debug: Log when component receives open prop

  const handleClose = () => {
    notifyVirtualTryOnClosed();
    setStep(1);
    setPersonImage(null);
    setPersonImagePreview(null);
    setResultImage(null);
    setImageError(false);
    setShowUploadArea(false);
    onClose();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      setPersonImage(base64);
      setPersonImagePreview(reader.result);
      // Automatically call API with user_upload mode after image is selected
      handleTryOn("user_upload", base64);
    };
    reader.readAsDataURL(file);
  };

  const handleTryOn = async (mode = "default", imageBase64 = null) => {
    if (!article_id) {
      alert("Article ID is missing");
      return;
    }

    // For user_upload mode, person image is required
    if (mode === "user_upload" && !personImage && !imageBase64) {
      alert("Please upload a photo");
      return;
    }

    setStep(2);

    try {
      // Get customer name from cookies/user profile (for custom_model mode)
      const userProfile = getUserProfile();
      const customerName = userProfile
        ? formatCustomModelName(
            userProfile.username ||
              userProfile.customer_name ||
              userProfile.name ||
              userProfile.customer_id ||
              userProfile.first_name
          )
        : null;
      // Prepare payload based on mode
      let payload = {
        article_id: article_id,
      };

      // DEFAULT MODE: Only article_id (no mode field)
      if (mode === "default") {
        // payload already has article_id, no other fields needed
      }
      // CUSTOM_MODEL MODE: article_id + custom_model
      else if (mode === "custom_model") {
        if (!customerName) {
          alert("User profile not found. Please log in to use custom model.");
          setStep(1);
          return;
        }
        payload.custom_model = customerName;
      }
      // USER_UPLOAD MODE: article_id + mode + person_image
      else if (mode === "user_upload") {
        payload.mode = "user_upload";
        payload.person_image = imageBase64 || personImage;
      }

      const response = await fetch(
        VIRTUAL_TRY_ON_URL,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        throw new Error(
          `Try-on failed: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      // Handle API response format:
      // For user_upload: { "available": true, "source": "generated", "image_base64": "<base64-string>" }
      // For default: { "available": true, "source": "generated", "image_url": "<url-string>" }
      let resultImageUrl = null;

      // Check for image_base64 first (user_upload mode response)
      if (data.image_base64) {
        // Convert base64 string to data URL for display
        if (data.image_base64.startsWith("data:")) {
          resultImageUrl = data.image_base64;
        } else {
          resultImageUrl = `data:image/png;base64,${data.image_base64}`;
        }
      }
      // Check for image_url (default mode response)
      else if (data.image_url) {
        // Clean the URL to fix Google Cloud Storage connection issues
        resultImageUrl = cleanImageUrl(data.image_url);
      }
      // Fallback to old formats if needed
      else if (
        data.images &&
        Array.isArray(data.images) &&
        data.images.length > 0
      ) {
        const firstImage = data.images[0];
        if (firstImage) {
          if (firstImage.startsWith("data:")) {
            resultImageUrl = firstImage;
          } else if (
            firstImage.startsWith("http://") ||
            firstImage.startsWith("https://")
          ) {
            resultImageUrl = cleanImageUrl(firstImage);
          } else {
            resultImageUrl = `data:image/png;base64,${firstImage}`;
          }
        }
      } else if (data.result_image) {
        if (data.result_image.startsWith("data:")) {
          resultImageUrl = data.result_image;
        } else if (
          data.result_image.startsWith("http://") ||
          data.result_image.startsWith("https://")
        ) {
          resultImageUrl = cleanImageUrl(data.result_image);
        } else {
          resultImageUrl = `data:image/png;base64,${data.result_image}`;
        }
      } else if (data.result) {
        if (data.result.startsWith("data:")) {
          resultImageUrl = data.result;
        } else if (
          data.result.startsWith("http://") ||
          data.result.startsWith("https://")
        ) {
          resultImageUrl = cleanImageUrl(data.result);
        } else {
          resultImageUrl = `data:image/png;base64,${data.result}`;
        }
      } else if (data.image) {
        if (data.image.startsWith("data:")) {
          resultImageUrl = data.image;
        } else if (
          data.image.startsWith("http://") ||
          data.image.startsWith("https://")
        ) {
          resultImageUrl = cleanImageUrl(data.image);
        } else {
          resultImageUrl = `data:image/png;base64,${data.image}`;
        }
      } else if (data.url) {
        resultImageUrl = cleanImageUrl(data.url);
      }

      if (resultImageUrl) {
        setResultImage(resultImageUrl);
        setImageError(false);
        setStep(3);
      } else {
        throw new Error(
          'No image result found in API response. Expected format: { available: true, source: "generated", image_url: "" }'
        );
      }
    } catch (error) {
      console.error("Try-on error:", error);
      notifyVirtualTryOnFailed();
      alert(error.message || "Failed to process try-on. Please try again.");
      setStep(1);
    }
  };

  const handleDownload = () => {
    if (!resultImage) return;

    const link = document.createElement("a");
    link.href = resultImage;
    link.download = "virtual-try-on-result.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTryAnother = () => {
    setStep(1);
    setPersonImage(null);
    setPersonImagePreview(null);
    setResultImage(null);
  };

  const handleBackToUpload = () => {
    setStep(1);
    // Keep the uploaded image and preview so user doesn't have to re-upload
    // setPersonImage(null);
    // setPersonImagePreview(null);
    setResultImage(null);
    setShowUploadArea(false);
  };

  const handleResultImageLoaded = () => {
    setImageError(false);
    notifyVirtualTryOnImageLoaded();
  };

  const handleResultImageError = () => {
    console.error("Failed to load result image:", resultImage);
    setImageError(true);
    notifyVirtualTryOnFailed();
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1300, // Material-UI modal z-index
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
      onClick={handleClose}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: "900px",
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "white",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: "#1E3A5F",
            color: "white",
            p: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Back Button - Show only in step 3 */}
            {step === 3 && (
              <IconButton
                onClick={handleBackToUpload}
                sx={{ color: "white", mr: 1 }}
                size="small"
              >
                <ArrowLeft size={20} />
              </IconButton>
            )}
            <Camera size={24} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Virtual Try-On
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                See how it looks on you instantly
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <X />
          </IconButton>
        </Box>

        {/* Progress Indicator */}
        <Box
          sx={{ p: 2, bgcolor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: step >= 1 ? "#1E3A5F" : "#999",
                fontWeight: step >= 1 ? "bold" : "normal",
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: step >= 1 ? "#1E3A5F" : "#ddd",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                {step > 1 ? <Check size={18} /> : "1"}
              </Box>
              <Typography>Upload Photo</Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                height: 2,
                bgcolor: step >= 2 ? "#1E3A5F" : "#ddd",
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: step >= 3 ? "#1E3A5F" : "#999",
                fontWeight: step >= 3 ? "bold" : "normal",
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: step >= 3 ? "#1E3A5F" : "#ddd",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                {step >= 3 ? <Check size={18} /> : "2"}
              </Box>
              <Typography>View Result</Typography>
            </Box>
          </Stack>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {step === 1 && (
            <Stack spacing={3}>
              {/* Show upload area only if user selected "Try on with Your Photo" */}
              {showUploadArea ? (
                <>
                  {/* Back Button */}
                  <Button
                    variant="text"
                    onClick={() => {
                      setShowUploadArea(false);
                      setPersonImage(null);
                      setPersonImagePreview(null);
                    }}
                    sx={{ alignSelf: "flex-start", color: "#1E3A5F" }}
                  >
                    ← Back
                  </Button>

                  {/* Upload Your Photo */}
                  <Paper
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: personImagePreview
                        ? "2px solid #1E3A5F"
                        : "2px dashed #ddd",
                      bgcolor: personImagePreview ? "#f0f7ff" : "white",
                    }}
                  >
                    {personImagePreview ? (
                      <Box>
                        <img
                          src={personImagePreview}
                          alt="Preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "300px",
                            borderRadius: "8px",
                            marginBottom: "16px",
                          }}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setPersonImage(null);
                            setPersonImagePreview(null);
                            fileInputRef.current?.click();
                          }}
                        >
                          Change Photo
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <Upload
                          size={48}
                          color="#1E3A5F"
                          style={{ marginBottom: "16px" }}
                        />
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          Upload Your Photo
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Choose a clear full-body photo for the best results
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Cloud />}
                          onClick={() => fileInputRef.current?.click()}
                          sx={{
                            mt: 2,
                            bgcolor: "#1E3A5F",
                            "&:hover": { bgcolor: "#152a47" },
                          }}
                        >
                          Choose Photo from Device
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={handleFileSelect}
                        />
                      </>
                    )}
                  </Paper>

                  {/* File Info */}
                  <Stack direction="row" spacing={3} justifyContent="center">
                    <Box sx={{ textAlign: "center" }}>
                      <Folder size={24} color="#8B4513" />
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        JPG, PNG
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Zap size={24} color="#FFD700" />
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        Max 10MB
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Shield size={24} color="#FFD700" />
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        100% Private
                      </Typography>
                    </Box>
                  </Stack>
                </>
              ) : (
                /* Show two option buttons initially */
                <>
                  {/* File Info */}
                  <Stack direction="row" spacing={3} justifyContent="center">
                    <Box sx={{ textAlign: "center" }}>
                      <Folder size={24} color="#8B4513" />
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        JPG, PNG
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Zap size={24} color="#FFD700" />
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        Max 10MB
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Shield size={24} color="#FFD700" />
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        100% Private
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Two Option Buttons */}
                  <Stack spacing={2}>
                  <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={() => handleTryOn("default")}
                      sx={{
                        borderColor: "#1E3A5F",
                        color: "#1E3A5F",
                        py: 2,
                        fontSize: "1rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        "&:hover": {
                          borderColor: "#152a47",
                          bgcolor: "#f0f7ff",
                        },
                      }}
                    >
                      Try on default model
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={() => handleTryOn("custom_model")}
                      sx={{
                        borderColor: "#1E3A5F",
                        color: "#1E3A5F",
                        py: 2,
                        fontSize: "1rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        "&:hover": {
                          borderColor: "#152a47",
                          bgcolor: "#f0f7ff",
                        },
                      }}
                    >
                      Try on with your profile picture
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={() => setShowUploadArea(true)}
                      sx={{
                        bgcolor: "#1E3A5F",
                        py: 2,
                        fontSize: "1rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        "&:hover": { bgcolor: "#152a47" },
                      }}
                    >
                     Upload an image and Try on
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          )}

          {step === 2 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CircularProgress size={60} sx={{ color: "#1E3A5F", mb: 3 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Processing Your Try-On
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This may take a few moments...
              </Typography>
            </Box>
          )}

          {step === 3 && (
            <Box>
              {/* Result */}
              <Paper sx={{ p: 2, textAlign: "center" }}>
                {resultImage ? (
                  <Box
                    sx={{
                      border: "2px solid #e0e0e0",
                      borderRadius: 2,
                      overflow: "hidden",
                      position: "relative",
                      minHeight: "500px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    {imageError ? (
                      <Box sx={{ textAlign: "center", p: 4 }}>
                        <Typography variant="body1" color="error" gutterBottom>
                          Failed to load image
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          The image cannot be accessed. This may be due to:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          component="div" 
                          sx={{ textAlign: "left", display: "inline-block", mt: 1, mb: 2 }}
                        >
                          <ul style={{ margin: 0, paddingLeft: "20px" }}>
                            <li>Image permissions issue (403 Forbidden)</li>
                            <li>Network connectivity problem</li>
                            <li>Image URL is invalid or expired</li>
                          </ul>
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setImageError(false);
                            // Force reload by creating a new image element
                            const img = new Image();
                            img.onload = () => {
                              setImageError(false);
                              notifyVirtualTryOnImageLoaded();
                            };
                            img.onerror = () => {
                              setImageError(true);
                              notifyVirtualTryOnFailed();
                            };
                            img.src = resultImage;
                          }}
                          sx={{ mt: 1 }}
                        >
                          Retry Loading Image
                        </Button>
                      </Box>
                    ) : (
                      <img
                        src={resultImage}
                        alt="Try-on result"
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                        onError={(e) => {
                          console.error("Image error event:", e);
                          handleResultImageError();
                        }}
                        onLoad={handleResultImageLoaded}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "70vh",
                          width: "auto",
                          height: "auto",
                          objectFit: "contain",
                        }}
                      />
                    )}
                    <Chip
                      label="Virtual Try-On"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        bgcolor: "rgba(0,0,0,0.7)",
                        color: "white",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: "400px",
                      bgcolor: "#f5f5f5",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography color="text.secondary">
                      No result available
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleDownload}
                  disabled={!resultImage}
                  fullWidth
                  sx={{ borderColor: "#1E3A5F", color: "#1E3A5F" }}
                >
                  Download Result
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RefreshCw />}
                  onClick={handleTryAnother}
                  fullWidth
                  sx={{ bgcolor: "#1E3A5F", "&:hover": { bgcolor: "#152a47" } }}
                >
                  Try Another
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default VirtualTryOn;
